<template>
  <div>
    <v-data-table :items="sortedQuestions"
                  :headers="headers"
                  :page="currentPage"
                  :must-sort="!searchQuery"
                  :sort-by="sort"
                  :custom-key-sort="{ priority: () => 0 }"
                  mobile-breakpoint="md"
                  :items-per-page="itemsPerPage"
                  class="questionAnswerDataTable"
                  @update:sort-by="onSortChange"
                  @click:row="onRowClick"
    >
      <template #no-data>
        {{ $t('project.question-answer.QuestionAnswerDataTable.noResults') }}
      </template>

      <template #[`header.id`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'id'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #[`header.priority`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'priority'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #[`header.question`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'question'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #[`header.reference`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'reference'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template v-if="isCurrentUserPm" #header.author="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'author'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        >
          <AppTooltip v-if="currentRoom?.hideQaAuthors">
            <template #activator="{ props }">
              <app-material-icon
                v-bind="props"
                data-locator="qa-authors-hidden-icon"
                name="visibility_off"
                size="xs"
                @click.stop
              />
            </template>
            <span>{{ $t('project.question-answer.QuestionAnswerDataTable.authorHiddenTooltip') }}</span>
          </AppTooltip>
        </DataTableSortHeader>
      </template>

      <template #[`header.share`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'share'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #[`header.createdAt`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'createdAt'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #[`header.lastUpdate`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'lastUpdate'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #[`header.status`]="{ column, toggleSort }">
        <DataTableSortHeader
          :column="column"
          :sort-key="'status'"
          :current-sort="sort"
          @toggle-sort="toggleSort"
        />
      </template>

      <template #item.id="{item}">
        <p class="text--medium">
        {{ `#${item.idInRoom}` }}
        </p>
      </template>
      <template #item.priority="{ item }">
        <div class="ml-2" data-locator="qa-priority-cell">
          <AppTooltip>
            <template #activator="{ props }">
              <app-material-icon
                v-bind="props"
                :name="getPriorityMeta(item).icon"
                :color="getPriorityMeta(item).iconColor"
                :data-locator="getPriorityMeta(item).locatorValue"
                size="sm"
                aria-hidden="true"
              />
            </template>
            {{ getPriorityMeta(item).fullLabel }}
          </AppTooltip>
        </div>
      </template>
      <template #item.question="{item}">
        <div class="questionAnswerDataTable-description d-inline-block"
             v-html="sanitizeHtml(item.description)"
        ></div>
        <div class="d-flex align-center">
          <div class="mr-5">
            <v-chip v-for="category in item.categories"
                    :key="category.id"
                    variant="flat"
                    size="small"
                    class="mr-1 questionAnswerDataTable-categoryChip"
            >
              {{ category.name }}
            </v-chip>
          </div>
        </div>
      </template>
      <template #item.reference="{item}">
        <AppTooltip v-if="item.file" :max-width="300">
          <template #activator="{ props }">
            <div class="questionAnswerDataTable-reference" v-bind="props">
              <div class="questionAnswerDataTable-reference--text">
                <span v-if="item.file.numbering">{{ item.file.numbering }}</span>
                {{ item.file.basename }}
              </div>
            </div>
          </template>
          <span>
            <span v-if="item.file.numbering">{{ item.file.numbering }}</span>
            {{ item.file.basename }}
          </span>
        </AppTooltip>

        <AppTooltip v-if="item.directory" :max-width="300">
          <template #activator="{ props }">
            <div class="questionAnswerDataTable-reference" v-bind="props">
              <div class="questionAnswerDataTable-reference--text">
                <span v-if="item.directory.numbering">{{ item.directory.numbering }}</span>
                {{ item.directory.name }}
              </div>
            </div>
          </template>
          <span>
            <span v-if="item.directory.numbering">{{ item.directory.numbering }}</span>
            {{ item.directory.name }}
          </span>
        </AppTooltip>
      </template>
      <template #item.author="{item}">
        <span v-if="!hideQaAuthorsForCurrentUser || item.userId === currentUserId">
          {{ item.userName }}
        </span>
        <span v-else>
          {{ $t('project.question-answer.QuestionAnswerDataTable.hiddenAuthor') }}
        </span>
      </template>

      <template #item.createdAt="{item}">
        <DateDisplay :value="item.createdAt"/>
      </template>

      <template #item.lastUpdate="{item}">
        <DateDisplay v-if="Date.parse(item.lastUpdate)" :value="item.lastUpdate" />
        <p class="text--medium" v-else>{{ item.lastUpdate }}</p>
      </template>
      <template #item.status="{item}">
        <div class="pl-4">
          <QuestionAnswerStatusIcon :status="item.status"/>
        </div>
      </template>

      <template #item.share="{item}">
        <div style="max-width: 120px">
          <RightsDisplayButton :rights="item.rights" @click.stop="onShareButtonClick(item)" />
        </div>
      </template>

      <template #item.actions="{item}">
        <div class="text-right">
          <app-button v-if="item.status !== QA_STATUS.VALIDATED"
                      class="px-4 py-2"
                      type="outlined"
                      variant="neutral"
                      size="small"
                      @click="onRowClick(item)"
          >
            {{ $t('project.question-answer.QuestionAnswerDataTable.answer') }}
          </app-button>
        </div>
      </template>

      <template #item.actionsDelete="{item}">
        <QuestionAnswerDataTableItemMenu
          v-if="isCurrentUserPm || item.rights.canWrite || item.userId === currentUserId"
          :item="item"
        />
      </template>
      <template #bottom>
        <div class="d-flex justify-end mt-4">
          <AppPagination v-model:current-page="currentPage"
                         v-model:page-size="itemsPerPage"
                         :total="sortedQuestions.length"
          />
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { mapGetters, mapState, useStore } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import AppPagination from '@/common/pagination/AppPagination.vue'
import { usePagination } from '@/common/pagination/usePagination'
import RightsDisplayButton from '@/common/users/RightsDisplayButton.vue'
import { QA_STATUS } from '@/common/utils/qa-status'
import { sanitizeHtml } from '@/common/helpers/htmlHelpers'
import { QAPriority, getQAPriorityMeta } from '@/project/question-answer/constants/qaPriority'
import QuestionAnswerStatusIcon from '@/project/question-answer/QuestionAnswerStatusIcon.vue'
import { QUESTION_ANSWER_ITEM_ROUTE_NAME } from '@/router/routerData'
import { QA_HIDDEN_AUTHOR_KEY } from '@/store/modules/question-answer/getters'

import QuestionAnswerDataTableItemMenu from './QuestionAnswerDataTableItemMenu.vue'
import DataTableSortHeader from '@/common/DataTableSortHeader.vue'

dayjs.extend(isBetween)
dayjs.extend(isSameOrAfter)

export default {
  name: 'QuestionAnswerDataTable',
  components: {
    AppPagination,
    AppTooltip,
    DateDisplay,
    QuestionAnswerStatusIcon,
    RightsDisplayButton,
    QuestionAnswerDataTableItemMenu,
    DataTableSortHeader,
  },
  setup: () => {
    const store = useStore()
    const { currentPage, itemsPerPage } = usePagination({
      currentUserId: store.getters['user/currentUserId'],
      userSettingsKey: 'qa-items-per-page',
    })

    return {
      currentPage,
      itemsPerPage,
      sanitizeHtml,
    }
  },
  props: {
    searchQuery: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      QA_STATUS,
      QA_HIDDEN_AUTHOR_KEY,
      headers: [
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.id'),
          value: 'id',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.priority'),
          value: 'priority',
          sortable: true,
          headerProps: { 'data-locator': 'qa-priority-column-header' },
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.question'),
          value: 'question',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.reference'),
          value: 'reference',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.author'),
          value: 'author',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.sharing'),
          value: 'share',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.createdAt'),
          value: 'createdAt',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.latestAddition'),
          value: 'lastUpdate',
          sortable: true,
        },
        {
          title: this.$t('project.question-answer.QuestionAnswerDataTable.headers.status'),
          value: 'status',
          sortable: true,
        },
        {
          value: 'actions',
          sortable: false,
        },
        {
          value: 'actionsDelete',
          sortable: false,
        },
      ],
      sort: [{ key: 'id', order: 'asc' }],
    }
  },
  computed: {
    ...mapState('questionAnswer', ['filters']),
    ...mapGetters('questionAnswer', ['questions']),
    ...mapGetters('user', ['currentUserId']),
    ...mapGetters('room', ['isCurrentUserPm']),
    ...mapState('room', ['currentRoom']),
    hideQaAuthorsForCurrentUser () {
      return !this.isCurrentUserPm && Boolean(this.currentRoom?.hideQaAuthors)
    },
    hiddenAuthorLabel () {
      return this.$t('project.question-answer.QuestionAnswerDataTable.hiddenAuthor')
    },
    filteredQuestions () {
      let filteredQuestions = this.questions
      const questionIdMatchSearchQueryFn = question => question.idInRoom.toString() === this.searchQuery
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()

        // Prevent users from accessing other authors questions (by typing a random user name)
        const canSearchInAuthorName = (q) => {
          if (!this.hideQaAuthorsForCurrentUser) return true
          return q.userId === this.currentUserId
        }

        filteredQuestions = filteredQuestions.filter(q => {
          return questionIdMatchSearchQueryFn(q) ||
            q.description.toLowerCase().includes(query) ||
            (canSearchInAuthorName(q) && (q.userName ?? '').toLowerCase().includes(query)) ||
            (q.file && q.file.basename.toLowerCase().includes(query)) ||
            q.categories.some(category => category.name.toLowerCase().includes(query))
        })

        if (filteredQuestions.find(elem => elem.id.toString().includes(this.searchQuery))) {
          filteredQuestions = filteredQuestions.sort((a, b) => {
            if (!a.id.toString().includes(this.searchQuery) && b.id.toString().includes(this.searchQuery)) return 1
            else if (a.id.toString().includes(this.searchQuery) && !b.id.toString().includes(this.searchQuery)) return -1
            else return 0
          })
        }
      }

      const categoriesFilters = this.filters.filter(filter => filter.category.key === 'categories')
      if (categoriesFilters.length > 0) {
        const categoryMatchFilter = category => categoriesFilters.some(categoryFilter => categoryFilter.value.key === category.id)
        filteredQuestions = filteredQuestions.filter(q => q.categories.some(categoryMatchFilter))
      }

      const questionersFilters = this.filters.filter(filter => filter.category.key === 'questioners')
      if (questionersFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => {
          return questionersFilters.some(filter => {
            const key = filter.value.key
            // Hidden authors mode:
            // - if "Auteur masqué" is selected, show only questions where author is not me
            // - if "me" is selected, show only questions where author is me
            if (this.hideQaAuthorsForCurrentUser) {
              if (key === this.QA_HIDDEN_AUTHOR_KEY) return q.userId !== this.currentUserId
              return q.userId === this.currentUserId
            }
            // Normal mode
            return key === q.userName
          })
        })
      }

      const createdAtFilter = this.filters.find(filter => filter.category.key === 'createdAt')
      if (createdAtFilter) {
        const filterFrom = createdAtFilter.value.key[0]
        const filterTo = createdAtFilter.value.key[1]

        if (filterFrom && !filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.createdAt
            return dayjs(qDate).isSameOrAfter(dayjs(filterFrom))
          })
        } else if (filterFrom && filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.createdAt
            return dayjs(qDate).isBetween(dayjs(filterFrom), dayjs(filterTo), 'day', '[]')
          })
        }
      }

      const lastUpdateFilter = this.filters.find(filter => filter.category.key === 'lastUpdate')
      if (lastUpdateFilter) {
        const filterFrom = lastUpdateFilter.value.key[0]
        const filterTo = lastUpdateFilter.value.key[1]

        if (filterFrom && !filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.lastUpdate
            return dayjs(qDate).isSameOrAfter(dayjs(filterFrom))
          })
        } else if (filterFrom && filterTo) {
          filteredQuestions = filteredQuestions.filter(q => {
            const qDate = q.lastUpdate
            return dayjs(qDate).isBetween(dayjs(filterFrom), dayjs(filterTo), 'day', '[]')
          })
        }
      }

      const referencesFilters = this.filters.filter(filter => filter.category.key === 'references')
      if (referencesFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => referencesFilters.some(filter => filter.value.key === q.file?.id))
      }

      const statusFilters = this.filters.filter(filter => filter.category.key === 'status')
      if (statusFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => statusFilters.some(filter => filter.value.key === q.status))
      }

      const priorityFilters = this.filters.filter(filter => filter.category.key === 'priority')
      if (priorityFilters.length > 0) {
        filteredQuestions = filteredQuestions.filter(q => priorityFilters.some(filter => filter.value.key === q.priority))
      }

      return filteredQuestions
    },
    sortedQuestions () {
      const items = [...this.filteredQuestions]
      const [sort] = this.sort
      const sortDesc = sort.order === 'desc'
      const exactIdIndex = items.findIndex(elem => elem.idInRoom.toString() === this.searchQuery)
      const exactIdItem = items[exactIdIndex]

      if (exactIdIndex !== -1) items.splice(exactIdIndex, 1)
      items.sort((a, b) => {
        if (sort.key === 'id') {
          if (sortDesc) {
            return b.idInRoom - a.idInRoom
          } else {
            return a.idInRoom - b.idInRoom
          }
        }
        if (sort.key === 'priority') {
          const rank = (p) => {
            if (p === QAPriority.LOW) return 1
            if (p === QAPriority.MEDIUM) return 2
            if (p === QAPriority.HIGH) return 3
            return 2
          }
          if (sortDesc) return rank(b.priority) - rank(a.priority)
          return rank(a.priority) - rank(b.priority)
        }
        if (sort.key === 'question') {
          if (sortDesc) {
            return b.description.localeCompare(a.description)
          } else {
            return a.description.localeCompare(b.description)
          }
        }
        if (sort.key === 'reference') {
          if (sortDesc) {
            return b.file?.basename?.localeCompare(a.file?.basename)
          } else {
            return a.file?.basename?.localeCompare(b.file?.basename)
          }
        }
        if (sort.key === 'author') {
          const nameA = (a.userName ?? this.hiddenAuthorLabel).toString()
          const nameB = (b.userName ?? this.hiddenAuthorLabel).toString()
          if (sortDesc) return nameB.localeCompare(nameA)
          return nameA.localeCompare(nameB)
        }
        if (sort.key === 'share') {
          const getAccessCount = (rights) => {
            if (rights.read) {
              return (rights.read.groups?.length ?? 0) + (rights.read.users?.length ?? 0)
            }
            return (rights.groups?.length ?? 0) + (rights.users?.length ?? 0)
          }
          const countA = getAccessCount(a.rights)
          const countB = getAccessCount(b.rights)
          if (sortDesc) return countB - countA
          return countA - countB
        }
        if (sort.key === 'createdAt') {
          if (sortDesc) {
            return dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1
          } else {
            return dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? -1 : 1
          }
        }
        if (sort.key === 'lastUpdate') {
          if (sortDesc) {
            return dayjs(a.lastUpdate).isBefore(dayjs(b.lastUpdate)) ? 1 : -1
          } else {
            return dayjs(a.lastUpdate).isBefore(dayjs(b.lastUpdate)) ? -1 : 1
          }
        }
        if (sort.key === 'status') {
          if (sortDesc) {
            return this.$t(`common.qaStatus.${b.status}`).localeCompare(this.$t(`common.qaStatus.${a.status}`))
          } else {
            return this.$t(`common.qaStatus.${a.status}`).localeCompare(this.$t(`common.qaStatus.${b.status}`))
          }
        }
      })
      if (exactIdIndex !== -1) items.unshift(exactIdItem)
      return items
    },
  },
  methods: {
    getPriorityMeta (item) {
      const p = item.priority || QAPriority.MEDIUM
      return getQAPriorityMeta(p, this.$t)
    },
    onRowClick (item, vuetifyRowObject = null) {
      // Badly documented. If we use the @click:row handler, we have the original event and the row object containing the item
      // As a second argument. So we'll only have the second argument when using the direct Vuetify event.
      // Source: https://github.com/vuetifyjs/vuetify/blob/2368668d49b322a1ac7aa5a9d602676affdef9b2/packages/api-generator/src/locale/en/VDataTable.json#L69
      if (vuetifyRowObject) {
        item = vuetifyRowObject.item
      }
      this.$router.push({
        name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
        params: {
          questionId: item.id,
        },
      })
    },
    onShareButtonClick (item) {
      this.$router.push({
        name: QUESTION_ANSWER_ITEM_ROUTE_NAME,
        params: {
          questionId: item.id,
        },
        query: {
          openSharePane: true,
        },
      })
    },
    onSortChange (sort) {
      this.sort = sort
    },
  },
}
</script>

<style scoped lang="scss">
.questionAnswerDataTable {
  background-color: transparent;

  :deep(table) {
    border-spacing: 0 6px;

    thead {
      th {
        font-size: 14px;
        font-weight: 500;
        color: rgb(var(--v-theme-text-primary)) !important;
        border: none !important;
        white-space: nowrap;
      }
    }

    tbody {
      tr {
        background: rgb(var(--v-theme-surface-primary));
        transition: box-shadow .5s;

        &:hover {
          box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
        }
      }

      td {
        color: rgb(var(--v-theme-text-secondary));
        border: none !important;
        padding-top: 16px !important;
        padding-bottom: 16px !important;

        &:last-child {
          border-top-right-radius: 6px;
          border-bottom-right-radius: 6px;
        }
      }
    }
  }
}

.questionAnswerDataTable-categoryChip {
  background-color: rgb(var(--v-theme-surface-grey-subtle)) !important;
  color: rgb(var(--v-theme-text-grey)) !important;
  border: 1px solid rgb(var(--v-theme-border-grey));
  font-weight: 700;
  font-size: 12px;
}
</style>
